import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, Modal, IconButton, CircularProgress, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useAppContext } from '../../../contexts/AppContext';
import { apiPost } from '../../../utils/api';
import { formatDateToLocal } from '../../../utils/dateUtils';

const SegmentationResults = ({ colors, segmentationVisualizations, segmentationCurrentRun }) => {
  // Debug logging for props
  console.log('[SEG_RESULTS] Component rendered with props:', {
    hasColors: !!colors,
    visualizationsCount: segmentationVisualizations?.length || 0,
    hasSegmentationCurrentRun: !!segmentationCurrentRun,
    segmentationCurrentRunType: typeof segmentationCurrentRun,
    segmentationCurrentRunValue: segmentationCurrentRun
  });
  
  const [imageUrls, setImageUrls] = useState({});
  const [imageBlobs, setImageBlobs] = useState({}); // Store actual blobs for download
  const [loadingImages, setLoadingImages] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  
  // Clear in-memory cache (images are stored in component state, not localStorage)
  // localStorage is not suitable for large binary data like images
  const clearCache = () => {
    console.log('[CACHE] Clearing in-memory image cache...');
    
    // Revoke all blob URLs to free memory
    Object.values(imageUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    
    // Clear state
    setImageUrls({});
    setImageBlobs({});
    setLoadingImages({});
    
    console.log('[CACHE] In-memory cache cleared');
  };
  
  // Expose clearCache to parent component via window for TrainingControlPanel
  useEffect(() => {
    window.clearSegmentationCache = clearCache;
    return () => {
      delete window.clearSegmentationCache;
    };
  }, []);

  // Extract comprehensive training metrics from segmentation run data
  const getTrainingMetrics = () => {
    console.log('[METRICS DEBUG] Starting getTrainingMetrics...');
    console.log('[METRICS DEBUG] segmentationCurrentRun type:', typeof segmentationCurrentRun);
    console.log('[METRICS DEBUG] segmentationCurrentRun value:', segmentationCurrentRun);
    
    if (!segmentationCurrentRun) {
      console.log('[METRICS DEBUG] No segmentationCurrentRun available - returning null');
      return null;
    }

    console.log('[METRICS DEBUG] segmentationCurrentRun keys:', Object.keys(segmentationCurrentRun));
    console.log('[METRICS DEBUG] Full segmentationCurrentRun object:', JSON.stringify(segmentationCurrentRun, null, 2));

    let metrics = {};

    // First, try to get metrics from completed training summary
    if (segmentationCurrentRun.summary && segmentationCurrentRun.summary.metrics) {
      console.log('[METRICS DEBUG] Found summary.metrics:', segmentationCurrentRun.summary.metrics);
      const summaryMetrics = segmentationCurrentRun.summary.metrics;
      
      // Map backend metric names to frontend display names
      if (summaryMetrics.val_loss !== undefined) metrics.val_loss = summaryMetrics.val_loss;
      if (summaryMetrics.train_loss !== undefined) metrics.train_loss = summaryMetrics.train_loss;
      if (summaryMetrics.val_dice_soft !== undefined) metrics.val_dice_soft = summaryMetrics.val_dice_soft * 100; // Convert to percentage
      if (summaryMetrics.val_dice_hard !== undefined) metrics.val_dice_hard = summaryMetrics.val_dice_hard * 100; // Convert to percentage
      if (summaryMetrics.val_iou_hard !== undefined) metrics.val_iou = summaryMetrics.val_iou_hard * 100; // Convert to percentage
      if (summaryMetrics.best_dice !== undefined) metrics.best_dice = summaryMetrics.best_dice * 100; // Convert to percentage
      if (summaryMetrics.learning_rate !== undefined) metrics.learning_rate = summaryMetrics.learning_rate;
      
      // Add epoch information from summary
      if (summaryMetrics.total_epochs !== undefined) {
        metrics.total_epochs = summaryMetrics.total_epochs;
        metrics.current_epoch = summaryMetrics.current_epoch || summaryMetrics.total_epochs; // Use current_epoch if available
      } else if (segmentationCurrentRun.summary.total_epochs) {
        metrics.total_epochs = segmentationCurrentRun.summary.total_epochs;
        metrics.current_epoch = segmentationCurrentRun.summary.total_epochs; // Completed training
      }
    }

    // Check if we have any metrics from the top level of segmentationCurrentRun
    if (Object.keys(metrics).length === 0) {
      console.log('[METRICS DEBUG] No summary metrics found, checking top-level fields');
      console.log('[METRICS DEBUG] Checking for direct metrics in segmentationCurrentRun...');
      
      // Check for metrics directly in the run object
      if (segmentationCurrentRun.val_loss !== undefined) {
        metrics.val_loss = segmentationCurrentRun.val_loss;
        console.log('[METRICS DEBUG] Found val_loss:', segmentationCurrentRun.val_loss);
      }
      if (segmentationCurrentRun.train_loss !== undefined) {
        metrics.train_loss = segmentationCurrentRun.train_loss;
        console.log('[METRICS DEBUG] Found train_loss:', segmentationCurrentRun.train_loss);
      }
      if (segmentationCurrentRun.val_dice_soft !== undefined) {
        metrics.val_dice_soft = segmentationCurrentRun.val_dice_soft * 100;
        console.log('[METRICS DEBUG] Found val_dice_soft:', segmentationCurrentRun.val_dice_soft);
      }
      if (segmentationCurrentRun.val_dice_hard !== undefined) {
        metrics.val_dice_hard = segmentationCurrentRun.val_dice_hard * 100;
        console.log('[METRICS DEBUG] Found val_dice_hard:', segmentationCurrentRun.val_dice_hard);
      }
      if (segmentationCurrentRun.val_iou_hard !== undefined) {
        metrics.val_iou = segmentationCurrentRun.val_iou_hard * 100;
        console.log('[METRICS DEBUG] Found val_iou_hard:', segmentationCurrentRun.val_iou_hard);
      }
      if (segmentationCurrentRun.best_dice !== undefined) {
        metrics.best_dice = segmentationCurrentRun.best_dice * 100;
        console.log('[METRICS DEBUG] Found best_dice:', segmentationCurrentRun.best_dice);
      }
      if (segmentationCurrentRun.learning_rate !== undefined) {
        metrics.learning_rate = segmentationCurrentRun.learning_rate;
        console.log('[METRICS DEBUG] Found learning_rate:', segmentationCurrentRun.learning_rate);
      }
      
      // Check for epoch information
      if (segmentationCurrentRun.total_epochs) {
        metrics.total_epochs = segmentationCurrentRun.total_epochs;
        metrics.current_epoch = segmentationCurrentRun.current_epoch || segmentationCurrentRun.total_epochs;
      }
    }

    // Fallback: try to extract from progress messages (for ongoing training)
    if (Object.keys(metrics).length === 0 && segmentationCurrentRun.progress && segmentationCurrentRun.progress.message) {
      console.log('[DEBUG] Extracting from progress message:', segmentationCurrentRun.progress.message);
      const message = segmentationCurrentRun.progress.message;
      
      // Parse validation metrics from progress message
      const valLossMatch = message.match(/loss\(avg\)=([0-9.]+)/);
      const valDiceMatch = message.match(/hard_dice\(global\)=([0-9.]+)%/);
      const valIouMatch = message.match(/hard_iou\(global\)=([0-9.]+)%/);
      
      if (valLossMatch) metrics.val_loss = parseFloat(valLossMatch[1]);
      if (valDiceMatch) metrics.val_dice_hard = parseFloat(valDiceMatch[1]);
      if (valIouMatch) metrics.val_iou = parseFloat(valIouMatch[1]);

      // Parse epoch-wise metrics from training summary
      const epochMatch = message.match(/Epoch: (\d+)\/(\d+)/);
      const trainLossMatch = message.match(/Tr\.loss: ([0-9.]+)/);
      const valLossEpochMatch = message.match(/Val\.loss: ([0-9.]+)/);
      const valDiceSoftMatch = message.match(/Val\.Dice_soft\.: ([0-9.]+)%/);
      const bestDiceMatch = message.match(/Best Dice\.: ([0-9.]+)%/);
      const valDiceHardMatch = message.match(/Val\.Dice_hard\.: ([0-9.]+)%/);
      const valIouEpochMatch = message.match(/Val\.iou\.: ([0-9.]+)%/);
      const lrMatch = message.match(/lr: ([0-9.e-]+)/);

      if (epochMatch) {
        metrics.current_epoch = parseInt(epochMatch[1]);
        metrics.total_epochs = parseInt(epochMatch[2]);
      }
      if (trainLossMatch) metrics.train_loss = parseFloat(trainLossMatch[1]);
      if (valLossEpochMatch) metrics.val_loss = parseFloat(valLossEpochMatch[1]);
      if (valDiceSoftMatch) metrics.val_dice_soft = parseFloat(valDiceSoftMatch[1]);
      if (bestDiceMatch) metrics.best_dice = parseFloat(bestDiceMatch[1]);
      if (valDiceHardMatch) metrics.val_dice_hard = parseFloat(valDiceHardMatch[1]);
      if (valIouEpochMatch) metrics.val_iou = parseFloat(valIouEpochMatch[1]);
      if (lrMatch) metrics.learning_rate = parseFloat(lrMatch[1]);
    }

    // Also check for metrics in progress object
    if (segmentationCurrentRun.progress && segmentationCurrentRun.progress.metrics) {
      console.log('[DEBUG] Found progress metrics:', segmentationCurrentRun.progress.metrics);
      Object.assign(metrics, segmentationCurrentRun.progress.metrics);
    }

    console.log('[METRICS DEBUG] Final extracted metrics count:', Object.keys(metrics).length);
    console.log('[METRICS DEBUG] Final extracted metrics:', metrics);
    
    if (Object.keys(metrics).length > 0) {
      console.log('[METRICS DEBUG] Returning metrics object');
      return metrics;
    } else {
      console.log('[METRICS DEBUG] No metrics found, returning null');
      return null;
    }
  };

  const trainingMetrics = getTrainingMetrics();

  // Handle image modal
  const handleOpenModal = (viz) => {
    setModalImage(viz);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalImage(null);
  };

  // Download a single image using FileReader data URL approach
  const downloadImage = (filename) => {
    console.log('[DOWNLOAD] Starting download for:', filename);
    
    const blob = imageBlobs[filename];
    const blobUrl = imageUrls[filename];
    
    if (!blob) {
      console.error('[DOWNLOAD] No blob available for:', filename);
      alert('Image not loaded yet. Please wait for images to load before downloading.');
      return;
    }
    
    // Detect Brave browser
    const isBrave = navigator.brave && typeof navigator.brave.isBrave === 'function';
    console.log('[DOWNLOAD] Browser detection - is Brave:', isBrave);
    console.log('[DOWNLOAD] Using FileReader data URL approach');
    console.log('[DOWNLOAD] Blob size:', blob.size, 'type:', blob.type);
    
    // Use FileReader to convert blob to data URL
    const reader = new FileReader();
    
    reader.onload = (e) => {
      console.log('[DOWNLOAD] FileReader loaded, data URL length:', e.target.result.length);
      
      // Create download link with data URL
      const link = document.createElement('a');
      link.href = e.target.result;
      link.download = filename;
      
      // Append, click, and remove
      document.body.appendChild(link);
      console.log('[DOWNLOAD] Triggering download...');
      link.click();
      console.log('[DOWNLOAD] Click executed');
      
      // Check after delay if download might have been blocked (Brave browser issue)
      setTimeout(() => {
        document.body.removeChild(link);
        console.log('[DOWNLOAD] \u2713 Download triggered for:', filename);
        
        // If Brave browser, show helpful message
        if (isBrave) {
          console.warn('[DOWNLOAD] Brave browser detected - downloads may be blocked by shields');
          // Open in new tab as fallback
          setTimeout(() => {
            const newTab = window.open(blobUrl, '_blank');
            if (newTab) {
              console.log('[DOWNLOAD] Opened image in new tab (Brave fallback)');
              alert(`Brave browser detected!\n\nIf download didn\'t start:\n1. Click Brave Shields icon (\u{1F6E1}\uFE0F) in address bar\n2. Turn OFF shields for localhost\n3. Or right-click the image in the new tab and "Save Image As"`);
            }
          }, 500);
        }
      }, 100);
    };
    
    reader.onerror = (error) => {
      console.error('[DOWNLOAD] FileReader error:', error);
      // Fallback: open in new tab
      const newTab = window.open(blobUrl, '_blank');
      if (newTab) {
        alert(`Download failed. Image opened in new tab.\n\nRight-click the image and select "Save Image As".`);
      } else {
        alert(`Download failed: ${error.message}\n\nPlease right-click the image and select "Save Image As".`);
      }
    };
    
    // Start reading the blob as data URL
    reader.readAsDataURL(blob);
  };

  // Download all images as a zip file
  const downloadAllImages = async () => {
    if (!segmentationVisualizations || segmentationVisualizations.length === 0) {
      alert('No images to download');
      return;
    }

    try {
      console.log('[DOWNLOAD_ALL] Starting ZIP creation...');
      console.log('[DOWNLOAD_ALL] Available images:', segmentationVisualizations.length);
      console.log('[DOWNLOAD_ALL] Loaded blobs:', Object.keys(imageBlobs).length);
      
      // Import JSZip dynamically
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let addedCount = 0;
      
      // Add all images to ZIP
      for (const viz of segmentationVisualizations) {
        const blob = imageBlobs[viz.filename];
        
        if (!blob) {
          console.warn(`[DOWNLOAD_ALL] Skipping ${viz.filename} - not loaded yet`);
          continue;
        }
        
        zip.file(viz.filename, blob);
        addedCount++;
        console.log(`[DOWNLOAD_ALL] ✓ Added ${viz.filename} (${blob.size} bytes)`);
      }
      
      if (addedCount === 0) {
        throw new Error('No images could be added to ZIP');
      }
      
      console.log(`[DOWNLOAD_ALL] Added ${addedCount}/${segmentationVisualizations.length} images to ZIP`);
      
      // Generate ZIP file
      console.log('[DOWNLOAD_ALL] Generating ZIP file...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      console.log('[DOWNLOAD_ALL] ZIP created, size:', zipBlob.size, 'bytes');
      
      // Try multiple download strategies
      try {
        // Strategy 1: Standard download
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'segmentation_results.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          console.log('[DOWNLOAD_ALL] ✓ ZIP download completed');
        }, 1000);
        
      } catch (downloadError) {
        console.warn('[DOWNLOAD_ALL] Standard download failed, trying data URL...');
        
        // Strategy 2: Data URL
        const reader = new FileReader();
        reader.onload = function(e) {
          const link = document.createElement('a');
          link.href = e.target.result;
          link.download = 'segmentation_results.zip';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('[DOWNLOAD_ALL] ✓ ZIP download completed via data URL');
        };
        reader.readAsDataURL(zipBlob);
      }
      
    } catch (error) {
      console.error('[DOWNLOAD_ALL] ZIP creation/download failed:', error);
      alert(`ZIP download failed: ${error.message}\n\nTrying individual downloads instead...`);
      
      // Fallback: download images individually
      console.log('[DOWNLOAD_ALL] Falling back to individual downloads...');
      for (const viz of segmentationVisualizations) {
        if (imageBlobs[viz.filename]) {
          downloadImage(viz.filename);
          // Small delay between downloads to avoid overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    }
  };

  // Load image function for displaying images
  const loadImage = async (viz) => {
    const cacheKey = viz.filename;
    
    console.log('[IMAGE DEBUG] Starting loadImage for:', viz.filename);
    console.log('[IMAGE DEBUG] viz object:', viz);
    console.log('[IMAGE DEBUG] viz.url:', viz.url);
    console.log('[IMAGE DEBUG] Current imageUrls state:', Object.keys(imageUrls));
    console.log('[IMAGE DEBUG] Cache key:', cacheKey);
    
    if (imageUrls[cacheKey] !== undefined) {
      console.log('[IMAGE DEBUG] Image already loaded or loading, skipping:', cacheKey);
      return; // Already loaded or loading
    }

    console.log('[IMAGE DEBUG] Setting loading state for:', cacheKey);
    setLoadingImages(prev => ({ ...prev, [cacheKey]: true }));

    try {
      // The backend returns URLs like '/api/static/seg_viz/...'
      // But apiPost adds API_BASE which already includes '/api'
      // So we need to strip the leading '/api' to avoid '/api/api/'
      let imageUrl = viz.url;
      if (imageUrl.startsWith('/api/')) {
        imageUrl = imageUrl.substring(4); // Remove '/api' prefix
      }
      console.log(`[IMAGE DEBUG] Original URL: ${viz.url}`);
      console.log(`[IMAGE DEBUG] Adjusted URL: ${imageUrl}`);
      
      // Use apiPost to make authenticated request (even though endpoint doesn't require auth, this maintains consistency)
      const blob = await apiPost(imageUrl, null, 'GET', 'blob');
      console.log(`[IMAGE DEBUG] Successfully loaded image: ${viz.filename}`);
      console.log(`[IMAGE DEBUG] Blob size:`, blob.size, 'bytes');
      
      // Create object URL for display
      const objectUrl = URL.createObjectURL(blob);
      console.log(`[IMAGE DEBUG] Created object URL:`, objectUrl);
      
      // Store BOTH the blob URL (for display) AND the blob itself (for download)
      setImageUrls(prev => {
        const newState = { ...prev, [cacheKey]: objectUrl };
        console.log('[IMAGE DEBUG] Updated imageUrls state:', Object.keys(newState));
        return newState;
      });
      
      setImageBlobs(prev => {
        const newState = { ...prev, [cacheKey]: blob };
        console.log('[IMAGE DEBUG] Updated imageBlobs state:', Object.keys(newState));
        return newState;
      });
      
      console.log(`[IMAGE DEBUG] Successfully loaded image: ${viz.filename}`);
      console.log(`[IMAGE DEBUG] Stored blob size:`, blob.size, 'type:', blob.type);
    } catch (error) {
      console.error(`[IMAGE DEBUG] Failed to load image ${viz.filename}:`, error);
      console.log(`[IMAGE DEBUG] Error details:`, {
        message: error.message,
        status: error.status,
        url: viz.url
      });
      setImageUrls(prev => ({ ...prev, [cacheKey]: null }));
    } finally {
      console.log('[IMAGE DEBUG] Setting loading state to false for:', cacheKey);
      setLoadingImages(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Monitor segmentationCurrentRun changes
  useEffect(() => {
    console.log('[SEG_RESULTS] segmentationCurrentRun prop changed:', {
      hasValue: !!segmentationCurrentRun,
      type: typeof segmentationCurrentRun,
      keys: segmentationCurrentRun ? Object.keys(segmentationCurrentRun) : 'null',
      value: segmentationCurrentRun
    });
  }, [segmentationCurrentRun]);

  // Load all images when component mounts or visualizations change
  useEffect(() => {
    if (segmentationVisualizations && segmentationVisualizations.length > 0) {
      // Check if we already have loaded images in memory
      const loadedCount = Object.keys(imageBlobs).length;
      console.log('[IMAGE] Loaded images in memory:', loadedCount, 'Total needed:', segmentationVisualizations.length);
      
      // Only load images that aren't already in memory
      if (loadedCount < segmentationVisualizations.length) {
        segmentationVisualizations.forEach((viz) => {
          if (!imageBlobs[viz.filename]) {
            loadImage(viz);
          }
        });
      }
    }

    // Cleanup object URLs when component unmounts
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [segmentationVisualizations]);

  if (!segmentationVisualizations || segmentationVisualizations.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 3, backgroundColor: 'var(--fx-paper)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ color: colors.grey[100] }}>
            Segmentation Results
          </Typography>
          
          {segmentationVisualizations && segmentationVisualizations.length > 0 && (
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={downloadAllImages}
              sx={{
                backgroundColor: colors.primary?.[600] || '#1976d2',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: colors.primary?.[700] || '#1565c0'
                }
              }}
            >
              Download All Images
            </Button>
          )}
        </Box>

        {/* Enhanced Training Metrics Display */}
        {trainingMetrics && (
          <Card sx={{ mb: 3, backgroundColor: colors.grey?.[800] || '#424242' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: colors.grey?.[100] || '#f5f5f5', mb: 3 }}>
                Training Results Summary
              </Typography>
              
              {/* Epoch Information */}
              {(trainingMetrics.current_epoch !== undefined || trainingMetrics.total_epochs !== undefined) && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: colors.grey?.[100] || '#f5f5f5', fontWeight: 'bold' }}>
                    Epoch {trainingMetrics.current_epoch || 1}/{trainingMetrics.total_epochs || 1}
                  </Typography>
                </Box>
              )}

              {/* Primary Metrics Row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {trainingMetrics.train_loss !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.primary?.[500] || '#1976d2', fontWeight: 'bold' }}>
                        {trainingMetrics.train_loss.toFixed(6)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Training Loss
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {trainingMetrics.val_loss !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.primary?.[400] || '#1565c0', fontWeight: 'bold' }}>
                        {trainingMetrics.val_loss.toFixed(6)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Validation Loss
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {trainingMetrics.val_dice_soft !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.secondary?.[500] || '#dc004e', fontWeight: 'bold' }}>
                        {trainingMetrics.val_dice_soft.toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Dice Soft
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {trainingMetrics.val_dice_hard !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.secondary?.[400] || '#f06292', fontWeight: 'bold' }}>
                        {trainingMetrics.val_dice_hard.toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Dice Hard
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Secondary Metrics Row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {trainingMetrics.val_iou !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.greenAccent?.[500] || '#4caf50', fontWeight: 'bold' }}>
                        {trainingMetrics.val_iou.toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        IoU Score
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {trainingMetrics.best_dice !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.secondary?.[300] || '#ba68c8', fontWeight: 'bold' }}>
                        {trainingMetrics.best_dice.toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Best Dice
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {trainingMetrics.learning_rate !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: colors.grey?.[900] || '#212121', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ color: colors.grey?.[300] || '#cccccc', fontWeight: 'bold' }}>
                        {trainingMetrics.learning_rate.toExponential(3)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        Learning Rate
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
        
        {/* Single Column Image Layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {segmentationVisualizations.map((viz, index) => {
            const cacheKey = viz.filename;
            const isLoading = loadingImages[cacheKey];
            const imageUrl = imageUrls[cacheKey];
            
            return (
              <Card key={index} sx={{ backgroundColor: colors.grey?.[900] || '#212121' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ color: colors.grey?.[100] || '#f5f5f5', mb: 1, textAlign: 'center' }}>
                    {viz.filename || `Visualization ${index + 1}`}
                  </Typography>
                  
                  <Box sx={{ 
                    textAlign: 'center', 
                    mb: 1, 
                    minHeight: '200px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: imageUrl ? 'pointer' : 'default'
                  }}
                  onClick={() => imageUrl && handleOpenModal(viz)}
                  >
                    {isLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: colors.primary?.[500] || '#1976d2' }} />
                        <Typography variant="body2" sx={{ color: colors.grey?.[400] || '#999999' }}>
                          Loading image...
                        </Typography>
                      </Box>
                    ) : imageUrl ? (
                      <img 
                        src={imageUrl}
                        alt={viz.filename || `Segmentation result ${index + 1}`}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '400px',
                          minHeight: '200px',
                          height: 'auto',
                          borderRadius: '4px',
                          border: `1px solid ${colors.grey?.[600] || '#666666'}`,
                          transition: 'transform 0.2s ease-in-out'
                        }}
                        onLoad={() => {
                          console.log(`Image loaded successfully: ${viz.filename}`);
                        }}
                        onError={(e) => {
                          console.error('Failed to display cached image:', viz);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 3 }}>
                        <Typography variant="body2" sx={{ color: colors.grey?.[400] || '#999999', textAlign: 'center' }}>
                          Image unavailable
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey?.[500] || '#777777', textAlign: 'center' }}>
                          {viz.filename}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {viz.metadata && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        <strong>Run ID:</strong> {viz.metadata.run_id || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        <strong>Epoch:</strong> {trainingMetrics.current_epoch || 1}/{trainingMetrics.total_epochs || 1 || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey?.[300] || '#cccccc' }}>
                        <strong>Created:</strong> {viz.metadata.created_at ? formatDateToLocal(viz.metadata.created_at) : 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Image Modal */}
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box sx={{
            position: 'relative',
            maxWidth: '95vw',
            maxHeight: '95vh',
            backgroundColor: colors.grey?.[900] || '#212121',
            borderRadius: 2,
            boxShadow: 24,
            outline: 'none'
          }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: colors.grey?.[100] || '#f5f5f5',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            
            <IconButton
              onClick={() => {
                if (modalImage) {
                  downloadImage(modalImage.filename);
                }
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 56,
                color: colors.grey?.[100] || '#f5f5f5',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
            
            {modalImage && (
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ 
                  color: colors.grey?.[100] || '#f5f5f5', 
                  mb: 2, 
                  textAlign: 'center',
                  pr: 6 // Account for close button
                }}>
                  {modalImage.filename}
                </Typography>
                <img
                  src={imageUrls[modalImage.filename] || modalImage.url}
                  alt={modalImage.filename}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '90vw',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              </Box>
            )}
          </Box>
        </Modal>
      </CardContent>
    </Card>
  );
};

export default SegmentationResults;
